import React, { useState, useContext } from 'react';
import { IcheerContext } from '../Context/AppContext'; // Adjust path as needed
import { User, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

const AdminLoginPage = () => {
  const { host, setSuperIsAdminLoggedIn } = useContext(IcheerContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async () => {
    if (!formData.username || !formData.password) {
      setMessage('Please fill in all fields');
      setMessageType('danger');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${host}admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      setMessage(data.message || 'Login successful');
      setMessageType('success'); 
      localStorage.setItem('adminUserName', formData.username);

      
      setSuperIsAdminLoggedIn(true);

      // Redirect to admin dashboard
      navigate('/');
    } catch (error) {
      setMessage(error.message || 'Login failed');
      setMessageType('danger');
    }
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center" style={{ 
      background: '#dc59a0'
    }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6">
            {/* Header */}
            <div className="text-center mb-4">
              <img src="https://cdn.shopify.com/s/files/1/0661/5177/6421/files/Logo_app.png" className='img-fluid'/>
            </div>
            
            {/* Login Card */}
            <div className="card shadow-lg border-0" style={{ borderRadius: '15px' }}>
              <div className="card-header bg-white border-0 text-center py-4" style={{ borderRadius: '15px 15px 0 0' }}>
                <h4 className="mb-0 text-dark fw-bold">Admin Sign In</h4>
              </div>
              
              <div className="card-body p-4">
                <div className="text-center mb-4">
                  <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                       style={{ width: '60px', height: '60px' }}>
                    <User size={24} className="text-primary" />
                  </div>
                  <p className="text-muted">Enter your credentials to access the admin panel</p>
                </div>

                <div className="mb-3">
                  <label htmlFor="username" className="form-label text-dark fw-semibold">
                    <User size={16} className="me-2" />
                    Username
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg border-0 shadow-sm"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter your username"
                    style={{ 
                      backgroundColor: '#f8f9fa',
                      borderRadius: '10px',
                      padding: '12px 16px'
                    }}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="password" className="form-label text-dark fw-semibold">
                    <Lock size={16} className="me-2" />
                    Password
                  </label>
                  <div className="position-relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-control form-control-lg border-0 shadow-sm"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Enter your password"
                      style={{ 
                        backgroundColor: '#f8f9fa',
                        borderRadius: '10px',
                        padding: '12px 45px 12px 16px'
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ border: 'none', background: 'none' }}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {message && (
                  <div className={`alert alert-${messageType} alert-dismissible border-0 shadow-sm mb-4`} role="alert">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">{message}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setMessage('')}
                      ></button>
                    </div>
                  </div>
                )}

                <button 
                  type="button"
                  className="btn btn-primary btn-lg w-100 fw-semibold shadow-sm mb-3"
                  onClick={handleLogin}
                  disabled={loading}
                  style={{ 
                    borderRadius: '10px',
                    padding: '12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none'
                  }}
                >
                  {loading ? (
                    <div className="d-flex align-items-center justify-content-center">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Signing in...
                    </div>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center">
                      <ArrowRight size={18} className="me-2" />
                      Sign In
                    </div>
                  )}
                </button>

                <div className="text-center mt-4">
                  <p className="text-muted mb-0">
                    Not an admin?{' '}
                    <a href="/login" className="text-decoration-none fw-semibold" 
                       style={{ color: '#667eea' }}>
                      Team login
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;